<template>
  <v-container fluid>
    <v-card class="mx-16 mt-6" max-height="calc(100vh - 100px)">
      <v-responsive class="overflow-y-auto">
        <v-data-table
          class="full-screen-table elevation-1 row-pointer"
          :headers="headers"
          :items="collaborators"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          hide-default-footer
          disable-sort
          fixed-header
          :loading="loadingCollaborators"
          :loading-text="getLabel('loading')"
          :no-data-text="getLabel('table_no_data')"
        >
          <template #item="{ item }">
            <tr @click="selectRow(item, $event)" @click.middle.prevent="goToCollaboratorProfile(item, $event)">
              <td v-for="header in headers" :key="header.value">
                <template v-if="header.value === 'sso_id'">
                  <v-switch readonly :input-value="item.sso_id" class="ma-0" dense hide-details />
                </template>
                <template v-else-if="header.value === 'firstname'">
                  <v-row justify="center">
                    {{ item.firstname + ' ' + item.lastname }}
                  </v-row>
                </template>
                <template v-else>
                  {{ item[header.value] }}
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-responsive>
      <v-container>
        <v-row justify="center">
          <v-col cols="6">
            <v-container class="max-width">
              <v-pagination v-if="totalPages != -1" v-model="page" :length="totalPages" />
            </v-container>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api';
import { useRouter, useRoute } from 'vue2-helpers/vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';

import { getGraphqlSchema } from '../../graphql/index';
import { getLabel, EventBus } from '@/utils/index';
import { useMainStore } from '@/store/index';

export default {
  name: 'TheDashboard',
  setup () {
    const router = useRouter();

    const route = useRoute();

    const routeQuery = route.query;

    const store = useMainStore();

    if (routeQuery.request === 'call_center') {
      store.callCenterMode = {
        isActive: true,
        returnUrl: routeQuery.redirect_url,
        marque: routeQuery.marque,
        orderDate: routeQuery.date,
        iresaCode: routeQuery.iresaCode
      };
    }

    if (routeQuery.request === 'hello') {
      store.helloMode = {
        isActive: true,
        returnUrl: process.env.VUE_APP_HELLO_URL,
        helloGroup: routeQuery.helloGroup
      };
    }

    if (route.name !== 'search') {
      store.cleanSearch();
    }

    const headers = [
      {
        text: getLabel('digital_account'),
        value: 'sso_id',
        align: 'start'
      },
      { text: getLabel('name'), value: 'firstname', align: 'center' },
      { text: getLabel('phone'), value: 'mobilephone' },
      { text: getLabel('email'), value: 'email' },
      { text: getLabel('registration_number'), value: 'registrationNumber' }
    ];

    const isToAddCollaboratorMode = () => store.addCollaboratorMode;

    const itemsPerPage = 15;

    const page = computed({
      get () {
        return store.search.page;
      },
      // setter
      set (newValue) {
        store.search.page = newValue;
      }
    });

    const totalCollaborators = ref(0);

    const variables = computed(() => ({
      query: {
        bool: {
            must: [{
              terms: {
                wtype: [
                  'collaborator'
                ]
              }
            },
            {
              bool: {
                should: [
                {
                  multi_match: {
                    query: store.search.query || '',
                    type: "cross_fields",
                    fields: ['firstname', 'lastname', 'email.raw', 'uuid', 'employeeNumber', 'mobilephone.raw'],
                    operator: "and",
                    boost: 100
                  }
                },
                {
                  script: {
                    script: {
                      source: `
                        String first = doc['firstname.raw'].size() > 0 ? doc['firstname.raw'].value : '';
                        String last = doc['lastname.raw'].size() > 0 ? doc['lastname.raw'].value : '';
                        String email = doc['email.raw'].size() > 0 ? doc['email.raw'].value : '';
                        String fullname = (first + ' ' + last).trim();
                        return fullname.toLowerCase().contains(params.search_term) || email.toLowerCase().contains(params.search_term);
                      `,
                      params: {
                        search_term: store.search.query.toLowerCase()
                      }
                    }
                  }
                }
              ]}
            }
          ] 
        }
      },
      from: (store.search.page * itemsPerPage) - (itemsPerPage),
      size: itemsPerPage,
      sort: [
        { 'firstname.raw': 'asc' }
      ]
    }));

    const { result, loading: loadingCollaborators, onResult } = useQuery(getGraphqlSchema('searchCollaborators'), variables, () => ({ fetchPolicy: 'no-cache' }));

    onResult(r => {
      if (r.loading) {
        return;
      }
      totalCollaborators.value = r.data.searchResults.count;
    });

    const collaborators = computed(() => (loadingCollaborators.value ? [] : result.value?.searchResults.items));

    const totalPages = computed(() => (totalCollaborators.value > 0 ? Math.ceil(totalCollaborators.value / itemsPerPage) : -1));

    // mutation that adds collaborator to group
    let collaboratorToAdd = null;

    const { mutate: mutationAddCollaboratorToGroup, onDone: onDoneAdd, onError: onErrorAdd } = useMutation(getGraphqlSchema('addCollaboratorToGroup'), () => ({
      variables: {
        uuid: store.group.uuid,
        collaborator: collaboratorToAdd.uuid
      }
    }));

    onErrorAdd(error => {
      store.loading = false;

      if (error.message === 'COLLABORATOR_ALREADY_IN_GROUP') {
        EventBus.$emit('show-notifications', [{
          message: getLabel('collaborator_in_group_error'),
          type: 'danger'
        }]);
      } else {
        EventBus.$emit('show-notifications', [{
          message: getLabel('collaborator_add_group_error'),
          type: 'danger'
        }]);
      }

      router.push({ name: 'manageGroup', params: { uuid: store.group.uuid } });
    });

    onDoneAdd(r => {
      store.loading = false;

      if (r.data.collaborator_add_collaborator_to_group.success) {
        store.group.collaborators = [...r.data.collaborator_add_collaborator_to_group.group.collaborators];

        EventBus.$emit('show-notifications', [{
          message: getLabel('collaborator_added_group'),
          type: 'success'
        }]);
      } else {
        EventBus.$emit('show-notifications', [{
          message: getLabel('collaborator_add_group_error'),
          type: 'danger'
        }]);
      }

      router.push({ name: 'manageGroup', params: { uuid: store.group.uuid } });
    });
    /** ********************************************* */

    const selectRow = (c, event) => {
      if (isToAddCollaboratorMode()) {
        collaboratorToAdd = c;

        store.loading = true;
        mutationAddCollaboratorToGroup();
      } else {
        goToCollaboratorProfile(c, event);
      }
    };

    const goToCollaboratorProfile = (c, event) => {
      const routerResolve = router.resolve({ name: 'manageProfile', params: { uuid: c.uuid } });

      // event button 1 is mouse middle click
      if (event.button === 1) {
        window.open(routerResolve.href, '_blank');
        return;
      }

      router.push(routerResolve.normalizedTo);
    }

    const updateCollaboratorList = updatedCollaborator => {
      result.value.searchResults.items = result.value.searchResults.items.map(i => {
        if (i.uuid === updatedCollaborator.uuid) {
          return updatedCollaborator;
        }
        return i;
      });
    };

    const deleteCollaboratorFromList = deletedCollaboratorUuid => {
      const indexToDelete = result.value.searchResults.items.findIndex(c => c.uuid === deletedCollaboratorUuid.uuid);

      result.value.searchResults.items.splice(indexToDelete, 1);
    };

    onMounted(() => {
      EventBus.$on('collaborator-delete', deleteCollaboratorFromList);

      EventBus.$on('update-collaborator', updateCollaboratorList);
    });

    return {
      getLabel,
      headers,
      isToAddCollaboratorMode,
      itemsPerPage,
      page,
      loadingCollaborators,
      result,
      collaborators,
      totalPages,
      selectRow,
      goToCollaboratorProfile
    };
  }
};
</script>

<style lang="scss">
.full-screen-table {
  div {
    max-height: calc(100vh - 230px);
  }
}

.row-pointer>.v-data-table__wrapper>table>tbody>tr:hover {
  cursor: pointer;
}
</style>
